import { AgGridModule } from '@ag-grid-community/angular';
import { ColDef, ModuleRegistry, ValueFormatterParams } from '@ag-grid-community/core';
import { ServerSideRowModelModule } from '@ag-grid-enterprise/server-side-row-model';
import { SetFilterModule } from '@ag-grid-enterprise/set-filter';
import { StatusBarModule } from '@ag-grid-enterprise/status-bar';
import { CommonModule } from '@angular/common';
import { Component, DestroyRef, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslateModule } from '@ngx-translate/core';
import { ChannelDistributionStatusService } from 'exports/services/channel-distribution-status.service';
import { BaseGrid } from 'shared/abstract/base-grid';

@Component({
    selector: 'app-channel-distribution-status',
    templateUrl: './channel-distribution-status.component.html',
    styleUrl: './channel-distribution-status.component.scss',
    imports: [TranslateModule, AgGridModule, CommonModule],
})
export class ChannelDistributionStatusComponent extends BaseGrid implements OnInit, OnDestroy {
    private updateInterval: any;

    public columnDefs: ColDef[] = [
        {
            field: 'name',
            headerName: 'Channel Name',
            cellRenderer: 'distributionTrackingChannelLogoCellRenderer',
            valueFormatter: (params: ValueFormatterParams) => {
                return params.data.channelName;
            },
        },
        {
            field: 'lastRunTimeOutput',
            headerName: 'Last checked',
            cellRenderer: 'translatedTextRenderer',
            valueFormatter: this.relativeTimeFormatter.bind(this),
            cellRendererParams: (params: any) => {
                const formattedDate = params.value ? new Date(params.value * 1000).toLocaleString() : '';
                return {
                    tooltipMessage: formattedDate,
                    useFormattedValue: true,
                };
            },
        },
        {
            field: 'nextRunTimeOutput',
            headerName: 'Next check',
            cellRenderer: 'translatedTextRenderer',
            valueFormatter: this.relativeTimeFormatter.bind(this),
            cellRendererParams: (params: any) => {
                const formattedDate = params.value ? new Date(params.value * 1000).toLocaleString() : '';
                return {
                    tooltipMessage: formattedDate,
                    useFormattedValue: true,
                };
            },
        },
        { field: 'description', width: 500, cellRenderer: 'translatedTextRenderer', headerName: 'Description' },
        {
            headerName: 'Status',
            field: 'trackingItemStatus',
            cellRenderer: 'distributionTrackingStatusCellRenderer',
            cellRendererParams: (params: any) => {
                return {
                    customTooltipMessage: params.data.customTooltipMessage,
                };
            },
            enableValue: true,
        },
    ];

    constructor(
        router: Router,
        activatedRoute: ActivatedRoute,
        destroyRef: DestroyRef,
        private channelDistributionStatusService: ChannelDistributionStatusService,
    ) {
        ModuleRegistry.registerModules([ServerSideRowModelModule, StatusBarModule, SetFilterModule]);
        super(channelDistributionStatusService, router, activatedRoute, destroyRef);
    }

    ngOnInit(): void {
        this.startRelativeTimeUpdates();
    }

    private startRelativeTimeUpdates(): void {
        // Update every minute (60000 milliseconds)
        this.updateInterval = setInterval(() => {
            if (this.agGrid?.api) {
                // Refresh the grid to update the relative time displays
                this.agGrid.api.refreshCells({
                    columns: ['lastRunTimeOutput', 'nextRunTimeOutput'],
                    force: true,
                });
            }
        }, 60000);
    }

    private relativeTimeFormatter(params: ValueFormatterParams): string {
        const value = params.value;

        if (!value) {
            return '-';
        }

        // Convert unix timestamp to Date object
        const date = new Date(value * 1000);
        const now = new Date();
        const diffMs = now.getTime() - date.getTime();
        const diffMinutes = Math.floor(diffMs / (1000 * 60));
        const diffHours = Math.floor(diffMinutes / 60);
        const diffDays = Math.floor(diffHours / 24);

        // Determine if this is a past or future time
        const isPast = diffMs > 0;
        const absDiffMinutes = Math.abs(diffMinutes);
        const absDiffHours = Math.abs(diffHours);
        const absDiffDays = Math.abs(diffDays);

        if (absDiffMinutes < 1) {
            return isPast ? 'Just now' : 'In a moment';
        } else if (absDiffMinutes < 60) {
            return isPast
                ? `${absDiffMinutes} minute${absDiffMinutes !== 1 ? 's' : ''} ago`
                : `In ${absDiffMinutes} minute${absDiffMinutes !== 1 ? 's' : ''}`;
        } else if (absDiffHours < 24) {
            return isPast
                ? `${absDiffHours} hour${absDiffHours !== 1 ? 's' : ''} ago`
                : `In ${absDiffHours} hour${absDiffHours !== 1 ? 's' : ''}`;
        } else if (absDiffDays < 7) {
            return isPast
                ? `${absDiffDays} day${absDiffDays !== 1 ? 's' : ''} ago`
                : `In ${absDiffDays} day${absDiffDays !== 1 ? 's' : ''}`;
        } else {
            // For longer periods, show the actual date
            return date.toLocaleDateString();
        }
    }

    override ngOnDestroy(): void {
        // Clean up the interval
        if (this.updateInterval) {
            clearInterval(this.updateInterval);
        }
        super.ngOnDestroy();
    }
}
